import { useEffect, useLayoutEffect, useRef, useState, type FormEvent, type KeyboardEvent, type ReactNode } from "react";
import { Link, Navigate, useLocation, useParams, useSearchParams } from "react-router";
import type { Json } from "@obsn/shared/db";
import { AUFGABE, NEUE_AUFGABE, useDialog, type NeueAufgabeVorgabe } from "../../kern/dialog";
import { useAngemeldet } from "../../kern/sitzung";
import { usePersonen, type PersonKurz } from "../../kern/personen";
import { fehlertext } from "../../kern/fehler";
import { Kalender, type KalenderSprint } from "../../kern/ui/Kalender";
import { PersonenAuswahl } from "../../kern/ui/PersonenAuswahl";
import { Haken } from "../../kern/ui/Haken";
import { Knopf } from "../../kern/ui/Knopf";
import { Meldung } from "../../kern/ui/Leer";
import { useHinweis } from "../../kern/ui/Hinweise";
import { Statuswahl } from "../../kern/ui/Statuswahl";
import { Wertwahl, type WertOption } from "../../kern/ui/Wertwahl";
import { useAufgabe, useAufgaben, useAufgabenAktionen, useFolge, useRessorts, useVorhaben, offeneUnteraufgaben } from "./daten";
import { KatalogAuswahl } from "./Katalog";
import { Menue, PAPIERKORB, type MenuePunkt } from "../../kern/ui/Menue";
import { linkKopieren } from "./Liste";
import { useLoeschen } from "./loeschen";
import { tagVon, wiederherstellenBis } from "./listenlogik";
import { anlegenVorschau, fortschritt, mitPunkt, hakenGesperrt, lastHinweis, PRIORITAET, SICHTBARKEIT, STATUS, STATUS_OPTIONEN, type Aufgabe, type Prioritaet, type Sichtbarkeit, type Status } from "./logik";
import { ortText } from "./sprintlogik";
import { titelHoeheSetzen } from "./titelhoehe";
import { grundSicht, sprintFuer, startwerte, type Wo } from "./vorgaben";
import { laufenderSprint, useSprints } from "./sprints";
import { useSchnellaktionen } from "./aktionen";
import { Aktivitaet } from "./Aktivitaet";
import { Rueckfrage } from "./Rueckfrage";
import ui from "../../kern/ui/ui.module.css";
import d from "./dialog.module.css";
import { Laedt } from "../../kern/ui/Laedt";
import { Reiter, reiterId } from "../../kern/ui/Reiter";
import { useKapsel } from "./vorhaben";
import { sichtErklaerung, sichtOptionen, waehlbareIds } from "./vorhabenlogik";

const PRIO_LISTE: Prioritaet[] = ["hoch", "normal", "niedrig"];
/** Wertwahl (BAUPLAN-FEINSCHLIFF.md, Abschnitt 3.3): Hoch mit Ton wie bisher (#FFF0EC, --accent-ink), Fahne wie bisher */
const PRIO_OPTIONEN: readonly WertOption<Prioritaet>[] = [
  { wert: "hoch", text: PRIORITAET.hoch, ton: "hoch", zeichen: "fahne" },
  { wert: "normal", text: PRIORITAET.normal, zeichen: "fahne" },
  { wert: "niedrig", text: PRIORITAET.niedrig, zeichen: "fahneLeer" },
];
const woWerte = (sprint: string | null): WertOption<"sprint" | "backlog">[] => [
  ...(sprint ? [{ wert: "sprint" as const, text: sprint, erklaerung: "Der laufende Sprint" }] : []),
  { wert: "backlog", text: "Backlog", erklaerung: "Noch ohne Sprint" },
];
const sichtWerte = (liste: readonly Sichtbarkeit[], vorhabenName: string | null): WertOption<Sichtbarkeit>[] =>
  liste.map((x) => ({ wert: x, text: SICHTBARKEIT[x], erklaerung: sichtErklaerung(x, vorhabenName) }));

/** /neu (alte Links, Lesezeichen): öffnet „Neue Aufgabe“ über Meine Woche, mit derselben Vorbelegung. */
export function NeuWeiterleitung() {
  const [params] = useSearchParams();
  const p = new URLSearchParams(params);
  p.set(NEUE_AUFGABE, "1");
  return <Navigate to={{ pathname: "/", search: `?${p.toString()}` }} replace />;
}

/** /aufgaben/:id (Mitteilungen, Mails): öffnet die Aufgabe als Dialog über Meine Woche. #k-<id> führt zum Kommentar. */
export function AufgabeWeiterleitung() {
  const { id } = useParams();
  const ort = useLocation();
  return <Navigate to={{ pathname: "/", search: `?${AUFGABE}=${encodeURIComponent(id ?? "")}`, hash: ort.hash }} replace />;
}

/** Eine Aufgabe als großer Dialog über der aktuellen Seite (AUFGABEN-STUFE2.md, Abschnitt 6): links die Aufgabe, rechts die Aktivität,
 *  am Handy mit Reitern. Hängt an der Adresse (?aufgabe=<id>, ?neu=1), kommt als Überlagerung aus dem Modul aufgaben.
 *  „Neue Aufgabe“ ist dieselbe Oberfläche im Entwurfsmodus und zeigt nach dem Anlegen die neue Aufgabe. */
export function AufgabeDialog() {
  const aufgabe = useDialog(AUFGABE);
  const neu = useDialog(NEUE_AUFGABE);
  const id = aufgabe.wert;
  const offen = !!id || neu.offen;
  const ref = useRef<HTMLDialogElement>(null);
  const zurueck = useRef<HTMLElement | null>(null);
  const [fokusKommentar, setFokusKommentar] = useState(false);

  useEffect(() => {
    const dlg = ref.current;
    if (!dlg) return;
    if (offen && !dlg.open) {
      zurueck.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      dlg.showModal();
    }
    if (!offen && dlg.open) {
      dlg.close();
      setFokusKommentar(false);
      // Fokus zurück auf das auslösende Element, wenn es noch da ist
      if (zurueck.current?.isConnected) zurueck.current.focus();
    }
  }, [offen]);

  const schliessen = id ? aufgabe.schliessen : neu.schliessen;

  return (
    <dialog
      ref={ref}
      className={d.dialog}
      aria-labelledby="dialog-name"
      onCancel={(e) => {
        e.preventDefault();
        schliessen();
      }}
    >
      {id ? (
        <Ansicht key={id} id={id} fokusKommentar={fokusKommentar} onSchliessen={schliessen} onWechseln={(x) => aufgabe.wechseln(AUFGABE, x)} />
      ) : neu.offen ? (
        <Entwurf
          vorgabe={neu.vorgabe}
          onSchliessen={schliessen}
          onAngelegt={(x) => {
            setFokusKommentar(true);
            neu.wechseln(AUFGABE, x);
          }}
        />
      ) : null}
    </dialog>
  );
}

/* ------------------------------------------------------------------------------------------------------------------------------ */
/* Rahmen: zwei Spalten, am Handy Reiter                                                                                           */
/* ------------------------------------------------------------------------------------------------------------------------------ */

function Rahmen({ links, rechts, anzahl }: { links: ReactNode; rechts: ReactNode; anzahl?: number | undefined }) {
  const [reiter, setReiter] = useState<"aufgabe" | "aktivitaet">("aufgabe");
  return (
    <div className={d.rahmen} data-reiter={reiter}>
      {/* Am Handy Reiter, am Desktop stehen beide Spalten nebeneinander */}
      <Reiter
        label="Aufgabe und Aktivität"
        className={d.reiter}
        eintraege={[
          { id: "aufgabe", label: "Aufgabe", panel: "spalte-aufgabe" },
          { id: "aktivitaet", label: anzahl ? `Aktivität (${anzahl})` : "Aktivität", panel: "spalte-aktivitaet" },
        ]}
        aktiv={reiter}
        onWechsel={setReiter}
        panel="spalte-aufgabe"
      />
      <section className={d.links} id="spalte-aufgabe" aria-labelledby={reiterId("spalte-aufgabe", "aufgabe")}>
        {links}
      </section>
      <aside className={d.rechts} id="spalte-aktivitaet" aria-labelledby={reiterId("spalte-aktivitaet", "aktivitaet")}>
        {rechts}
      </aside>
    </div>
  );
}

function Kopf({ brot, children, onSchliessen }: { brot: ReactNode; children?: ReactNode; onSchliessen: () => void }) {
  return (
    <div className={d.kopf}>
      <p className={d.brot}>{brot}</p>
      <span className={d.kopfKnoepfe}>
        {children}
        <button type="button" className={d.schliessen} aria-label="Schließen" onClick={onSchliessen}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>
      </span>
    </div>
  );
}

/** Titel als mehrzeiliges Feld, das mitwächst. Enter verlässt das Feld (im Entwurf: legt an). `rahmen`: Feld mit Rand (Neue Aufgabe). */
function Titel({
  wert,
  onWechsel,
  onFertig,
  gesperrt,
  platzhalter,
  rahmen,
}: {
  wert: string;
  onWechsel: (t: string) => void;
  onFertig: () => void;
  gesperrt?: boolean | undefined;
  platzhalter?: string | undefined;
  rahmen?: boolean | undefined;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);
  useLayoutEffect(() => {
    if (ref.current) titelHoeheSetzen(ref.current);
  }, [wert]);
  useEffect(() => {
    // Die Neue Aufgabe steht beim ersten Zeichnen in einem noch geschlossenen Fenster: Fokus erst im nächsten Bild, wenn es offen ist.
    // So merkt sich der Dialog vorher den Knopf, der ihn geöffnet hat, und gibt ihm beim Schließen den Fokus zurück.
    if (!rahmen) ref.current?.focus();
    // Nach dem Öffnen des Fensters und wenn die Schrift geladen ist, noch einmal messen
    const neu = () => {
      if (!ref.current) return;
      titelHoeheSetzen(ref.current);
      if (rahmen) ref.current.focus();
    };
    const bild = requestAnimationFrame(neu);
    void document.fonts?.ready.then(() => ref.current && titelHoeheSetzen(ref.current));
    return () => cancelAnimationFrame(bild);
  }, [rahmen]);
  return (
    <>
      <label className="nur-leser" htmlFor="aufgabe-titel">
        Titel
      </label>
      <textarea
        ref={ref}
        id="aufgabe-titel"
        className={[d.titel, rahmen && d.titelFeld].filter(Boolean).join(" ")}
        rows={1}
        value={wert}
        maxLength={200}
        disabled={gesperrt}
        placeholder={platzhalter}
        onChange={(e) => onWechsel(e.target.value.replace(/\n/g, " "))}
        onBlur={onFertig}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            (e.target as HTMLTextAreaElement).blur();
          }
        }}
      />
    </>
  );
}

function Zeile({ symbol, label, children, breit }: { symbol: ReactNode; label: string; children: ReactNode; breit?: boolean | undefined }) {
  return (
    <div className={[d.eigenschaft, breit && d.breit].filter(Boolean).join(" ")}>
      <span className={d.eigLabel}>
        {symbol}
        {label}
      </span>
      <div className={d.eigWert}>{children}</div>
    </div>
  );
}

const Symbol = {
  status: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="3" /></svg>,
  person: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><circle cx="12" cy="8" r="4" /><path d="M4 21c1.5-4 4.5-6 8-6s6.5 2 8 6" /></svg>,
  frist: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 10h18M8 3v4M16 3v4" /></svg>,
  prio: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M5 21V4h11l-2 4 2 4H5" /></svg>,
  ressort: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M3 7h6l2 2h10v10H3z" /></svg>,
  wo: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M4 6h16M4 12h10M4 18h6" /></svg>,
  sicht: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12z" /><circle cx="12" cy="12" r="3" /></svg>,
};

/** Wer erscheint in der Personenauswahl? Vorstand: alle Aktiven. Leitung eines Vorhabens (außer bei „Nur Vorstand“): die Personen
 *  daraus und sich. Sonst nur man selbst, und nur wer bearbeiten darf. */
function useWaehlbar(darf: boolean, kontext: { vorhaben: string | null; sichtbarkeit: Sichtbarkeit }) {
  const { person: ich, rechte } = useAngemeldet();
  const personen = usePersonen();
  const kapsel = useKapsel();
  const vorstand = rechte.has("aufgaben.alle_bearbeiten");
  const leitet = kapsel.leitet(kontext.vorhaben);
  const aktiv = (personen.data ?? []).filter((p) => p.aktiv);
  const erlaubt = new Set(
    waehlbareIds({ vorstand, ich: ich.id, aktive: aktiv.map((p) => p.id), leitet, sichtbarkeit: kontext.sichtbarkeit, imVorhaben: kapsel.personenVon(kontext.vorhaben) }),
  );
  const waehlbar: PersonKurz[] = !darf ? [] : aktiv.filter((p) => erlaubt.has(p.id));
  return { personen: personen.data, waehlbar, leitet, entfernbar: (pid: string) => darf && (vorstand || erlaubt.has(pid)) };
}

/** Hinweis aus KONZEPT: Wer ohne Vorstandsrolle eine Aufgabe „Nur Vorstand“ hat, sieht sie trotzdem. */
function sichtHinweis(personen: readonly PersonKurz[] | undefined, ids: readonly string[], sichtbarkeit: Sichtbarkeit): string | null {
  if (sichtbarkeit !== "vorstand") return null;
  const ohne = ids.map((id) => personen?.find((p) => p.id === id)).filter((p): p is PersonKurz => !!p && !p.vorstand);
  if (ohne.length === 0) return null;
  return ohne.map((p) => `${p.vorname} ist verantwortlich und sieht diese Aufgabe deshalb.`).join(" ");
}

function kalenderSprint(a: { sprint_id: string | null; sprint_name: string | null }, sprints: ReturnType<typeof useSprints>["data"]): KalenderSprint | null {
  const sp = sprints?.find((x) => x.id === a.sprint_id) ?? laufenderSprint(sprints);
  return sp ? { beginn: sp.beginn, ende: sp.ende, name: sp.name } : null;
}

/* ------------------------------------------------------------------------------------------------------------------------------ */
/* Ansicht einer bestehenden Aufgabe                                                                                               */
/* ------------------------------------------------------------------------------------------------------------------------------ */

function Ansicht({ id, fokusKommentar, onSchliessen, onWechseln }: { id: string; fokusKommentar: boolean; onSchliessen: () => void; onWechseln: (id: string) => void }) {
  const q = useAufgabe(id);
  if (q.isPending) {
    return (
      <div className={d.leer}>
        <h2 id="dialog-name" className="nur-leser">
          Aufgabe
        </h2>
        <Laedt />
      </div>
    );
  }
  if (q.isError || !q.data.aufgabe) {
    return (
      <div className={d.leer}>
        <h2 id="dialog-name">Diese Aufgabe gibt es nicht oder du siehst sie nicht.</h2>
        {q.isError ? <Meldung fehler>{fehlertext(q.error)}</Meldung> : null}
        <Knopf onClick={onSchliessen}>Schließen</Knopf>
      </div>
    );
  }
  return <Bestehend a={q.data.aufgabe} unteraufgaben={q.data.unteraufgaben} fokusKommentar={fokusKommentar} onSchliessen={onSchliessen} onWechseln={onWechseln} />;
}

function Bestehend({
  a,
  unteraufgaben,
  fokusKommentar,
  onSchliessen,
  onWechseln,
}: {
  a: Aufgabe;
  unteraufgaben: Aufgabe[];
  fokusKommentar: boolean;
  onSchliessen: () => void;
  onWechseln: (id: string) => void;
}) {
  const sitzung = useAngemeldet();
  const ich = sitzung.person.id;
  const vorstand = sitzung.rechte.has("aufgaben.alle_bearbeiten");
  const darfPlanen = sitzung.rechte.has("sprint.planen");
  const darfVorhaben = sitzung.rechte.has("vorhaben.verwalten");
  const { zeigen } = useHinweis();
  const sprints = useSprints();
  const ressorts = useRessorts();
  const vorhaben = useVorhaben();
  const kapsel = useKapsel();
  const folge = useFolge(a.id);
  const sa = useSchnellaktionen();
  const { akt } = sa;
  const loeschen = useLoeschen(sa);

  const imArchiv = a.sprint_status === "abgeschlossen";
  const geloescht = !!a.archiviert_am;
  // Die Leitung eines Vorhabens bearbeitet alle Aufgaben darin, die sie sieht (Paket C1)
  const leitet = kapsel.leitet(a.vorhaben_id);
  const darfGrundsaetzlich = vorstand || leitet || a.personen.includes(ich) || a.erstellt_von === ich;
  const darf = darfGrundsaetzlich && !imArchiv && !geloescht;
  const wahl = useWaehlbar(darf, { vorhaben: a.vorhaben_id, sichtbarkeit: a.sichtbarkeit });
  // Das Vorhaben ändert der Vorstand, sonst die Leitung des bisherigen (oder es gab keins), wie aufgaben_bearbeiten
  const darfVorhabenAendern = darf && (vorstand || !a.vorhaben_id || leitet);
  const vorhabenName = kapsel.name(a.vorhaben_id) ?? vorhaben.data?.find((x) => x.id === a.vorhaben_id)?.name ?? null;
  const laufend = laufenderSprint(sprints.data);
  const ressortName = ressorts.data?.find((r) => r.id === a.ressort_id)?.name;

  const [titel, setTitel] = useState(a.titel);
  const [beschreibung, setBeschreibung] = useState(a.beschreibung);
  const [grund, setGrund] = useState(a.status_grund);
  const [gespeichert, setGespeichert] = useState(false);
  const [rueckfrage, setRueckfrage] = useState<string | null>(null);

  const fehler = (e: unknown) => zeigen(fehlertext(e), { fehler: true });
  const speichern = (aenderung: Record<string, Json>, danach?: () => void) =>
    akt.bearbeiten.mutate({ id: a.id, aenderung }, { onError: fehler, ...(danach ? { onSuccess: danach } : {}) });

  /** Nach jedem Wechsel ein Hinweis mit „Rückgängig“, wie in den Listen (BAUPLAN-PAKETE.md, Abschnitt 4.1, BAUPLAN-STATUSWAHL.md, 2.3).
   *  Ändert sich nur der Grund bei „Wartet“, kommt keiner. */
  function status(neu: Status, mit?: boolean) {
    const vorher = a.status;
    const vorherGrund = a.status_grund;
    const zurueck = () =>
      akt.statusSofort.mutate({ id: a.id, status: vorher, ...(vorher === "wartet" ? { grund: vorherGrund } : {}) }, { onError: fehler });
    akt.statusSofort.mutate(
      { id: a.id, status: neu, ...(neu === "wartet" ? { grund: grund.trim() } : {}), ...(mit !== undefined ? { unteraufgabenMit: mit } : {}) },
      {
        onSuccess: () => {
          setRueckfrage(null);
          if (neu === "erledigt") zeigen(`„${a.titel}“ erledigt.`, { rueckgaengig: zurueck });
          else if (vorher === "erledigt") zeigen(`„${a.titel}“ ist wieder offen.`, { rueckgaengig: zurueck });
          else if (neu !== vorher) zeigen(`${a.titel}: ${STATUS[neu]}`, { rueckgaengig: zurueck });
        },
        onError: (e) => {
          const offen = offeneUnteraufgaben(e);
          if (offen) setRueckfrage(offen);
          else fehler(e);
        },
      },
    );
  }

  const gesperrt = geloescht
    ? "Diese Aufgabe ist gelöscht. Kommentare sind nur noch zu lesen."
    : imArchiv
      ? `Liegt im Archiv (${a.sprint_name ?? "Sprint"}). Kommentare sind nur noch zu lesen.`
      : null;

  const brot = a.eltern_id ? (
    <>
      Unteraufgabe von{" "}
      <button type="button" className={d.brotLink} onClick={() => a.eltern_id && onWechseln(a.eltern_id)}>
        {a.eltern_titel ?? "der Aufgabe"}
      </button>
    </>
  ) : (
    `${ortText(a)}${ressortName ? ` · ${ressortName}` : ""}`
  );

  // Menü ⋯ oben rechts (AUFGABEN-LISTE.md, Abschnitt 4): Link kopieren, Löschen oder Wiederherstellen
  const menue: MenuePunkt[] = [{ label: "Link kopieren", ausfuehren: () => void linkKopieren(a.id, zeigen) }];
  if (darfGrundsaetzlich && geloescht) menue.push({ label: "Wiederherstellen", ausfuehren: () => loeschen.wiederherstellen(a) });
  else if (darfGrundsaetzlich && !imArchiv)
    menue.push({ label: a.eltern_id ? "Unteraufgabe löschen" : "Aufgabe löschen", gefahr: true, symbol: PAPIERKORB, ausfuehren: () => loeschen.loeschen(a) });
  const loescher = geloescht ? wahl.personen?.find((p) => p.id === a.geaendert_von) : undefined;

  const links = (
    <div className={d.inhalt}>
      <h2 id="dialog-name" className="nur-leser">
        {a.eltern_id ? "Unteraufgabe" : "Aufgabe"}: {a.titel}
      </h2>
      <Kopf brot={brot} onSchliessen={onSchliessen}>
        <button
          type="button"
          className={d.glocke}
          aria-pressed={folge.data ?? false}
          aria-label="Folgen"
          title={folge.data ? "Du folgst" : "Folgen"}
          onClick={() =>
            akt.folgen.mutate(
              { id: a.id, an: !folge.data },
              { onSuccess: () => zeigen(folge.data ? "Du folgst nicht mehr." : "Du folgst. Änderungen und Kommentare kommen als Mitteilung."), onError: fehler },
            )
          }
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
            <path d="M6 16V11a6 6 0 1 1 12 0v5l2 2H4z" />
            <path d="M10 20a2 2 0 0 0 4 0" />
          </svg>
        </button>
        <Menue label={`Mehr zu „${a.titel}“`} punkte={menue} />
      </Kopf>

      {imArchiv || geloescht ? (
        <Meldung>
          {geloescht && a.archiviert_am
            ? `Gelöscht am ${tagVon(a.archiviert_am)}${loescher ? ` von ${loescher.vorname}` : ""}. ${
                a.vorhaben_id ? "Endgültig gelöscht wird sie mit dem Vorhaben." : `Wiederherstellen geht bis zum ${wiederherstellenBis(a.archiviert_am)}.`
              }`
            : `${a.status === "erledigt" ? "Erledigt in" : "Aus"} ${a.sprint_name ?? "einem Sprint"}, liegt im Archiv. Schreibgeschützt.`}{" "}
          {darfGrundsaetzlich && geloescht ? (
            <Knopf klein onClick={() => loeschen.wiederherstellen(a)}>
              Wiederherstellen
            </Knopf>
          ) : null}
          {darfGrundsaetzlich && imArchiv && !geloescht && !a.eltern_id && a.status === "erledigt" ? (
            <Knopf klein onClick={() => akt.wiederOeffnen.mutate(a.id, { onSuccess: () => zeigen("Wieder offen. Sie liegt im Backlog."), onError: fehler })}>Wieder öffnen</Knopf>
          ) : null}
        </Meldung>
      ) : null}

      <Titel
        wert={titel}
        gesperrt={!darf}
        onWechsel={setTitel}
        onFertig={() => {
          const t = titel.trim();
          if (!t) setTitel(a.titel);
          else if (t !== a.titel) speichern({ titel: t });
        }}
      />

      <div className={d.eigenschaften}>
        <Zeile symbol={Symbol.status} label="Status">
          <span className={d.reihe}>
            <Statuswahl name={a.titel} optionen={STATUS_OPTIONEN} wert={a.status} darf={darf} onWechsel={(neu) => status(neu)} />
            <Haken
              an={a.status === "erledigt"}
              titel={a.titel}
              gesperrt={hakenGesperrt(a, ich, vorstand, leitet)}
              onUmschalten={() => status(a.status === "erledigt" ? "zu_tun" : "erledigt")}
            />
          </span>
          {a.status === "wartet" ? (
            <label className={d.warum}>
              <span className={d.meta}>Warum? Worauf wartet sie?</span>
              <input
                className={d.eingabe}
                value={grund}
                maxLength={500}
                disabled={!darf}
                onChange={(e) => setGrund(e.target.value)}
                onBlur={() => grund.trim() !== a.status_grund && status("wartet")}
              />
            </label>
          ) : null}
        </Zeile>
        <Zeile symbol={Symbol.person} label="Verantwortlich" breit>
          <PersonenAuswahl
            label={`Verantwortlich für „${a.titel}“`}
            ausgewaehlt={a.personen}
            personen={wahl.personen}
            waehlbar={wahl.waehlbar}
            entfernbar={wahl.entfernbar}
            gesperrt={!darf}
            onHinzu={(pid) => sa.personHinzu(a, pid)}
            onWeg={(pid) => akt.personWeg.mutate({ id: a.id, person: pid }, { onError: fehler })}
          />
          {sichtHinweis(wahl.personen, a.personen, a.sichtbarkeit) ? <span className={d.meta}>{sichtHinweis(wahl.personen, a.personen, a.sichtbarkeit)}</span> : null}
        </Zeile>
        <Zeile symbol={Symbol.frist} label="Frist">
          <Kalender
            label="Frist"
            wert={a.frist_datum}
            leer="Frist setzen"
            entfernen="Frist entfernen"
            sprint={kalenderSprint(a, sprints.data)}
            gesperrt={!darf}
            zusatz={!a.frist_datum && a.sprint_name && !imArchiv ? `ohne Datum gilt Sprintende ${a.sprint_name.replace("Sprint ", "")}` : undefined}
            onWahl={(tag) => speichern({ frist_datum: tag })}
          />
        </Zeile>
        <Zeile symbol={Symbol.prio} label="Priorität">
          <Wertwahl art="Priorität" optionen={PRIO_OPTIONEN} wert={a.prioritaet} darf={darf} onWechsel={(neu) => speichern({ prioritaet: neu })} />
        </Zeile>
        <Zeile symbol={Symbol.ressort} label="Ressort">
          {a.eltern_id ? (
            <span>{ressortName ?? "Ohne Ressort"}, wie die Aufgabe</span>
          ) : (
            <KatalogAuswahl art="ressort" wert={a.ressort_id} eintraege={ressorts.data} darfAnlegen={darfPlanen && darf} gesperrt={!darf} onWechsel={(x) => speichern({ ressort_id: x })} />
          )}
        </Zeile>
        <Zeile symbol={Symbol.wo} label="Wo">
          {a.eltern_id ? (
            <span>{ortText(a)}, mit der Aufgabe</span>
          ) : darfPlanen && darf ? (
            <Wertwahl
              art="Wo"
              optionen={woWerte(laufend?.name ?? null)}
              wert={a.sprint_id ? "sprint" : "backlog"}
              onWechsel={(neu) =>
                neu === "sprint"
                  ? sa.inSprint(a, mitPunkt(`Jetzt in ${laufend?.name ?? "dem Sprint"}`))
                  : akt.inBacklog.mutate(a.id, { onSuccess: () => zeigen("Jetzt im Backlog."), onError: fehler })
              }
            />
          ) : (
            <span>{ortText(a)}</span>
          )}
        </Zeile>
        <Zeile symbol={Symbol.sicht} label="Sichtbar für" breit>
          {a.eltern_id ? (
            <span>{SICHTBARKEIT[a.sichtbarkeit]}, wie die Aufgabe</span>
          ) : (
            // Die Sichtbarkeit ändert der Vorstand. „Kapsel“ gibt es nur mit Vorhaben (BAUPLAN-PAKETE.md, Abschnitt 9.5).
            <Wertwahl
              art="Sichtbar für"
              optionen={sichtWerte(sichtOptionen(!!a.vorhaben_id, true), vorhabenName)}
              wert={a.sichtbarkeit}
              darf={darf && vorstand}
              onWechsel={(neu) => speichern({ sichtbarkeit: neu })}
            />
          )}
          <span className={d.meta}>{sichtErklaerung(a.sichtbarkeit, vorhabenName)}</span>
        </Zeile>
        {!a.eltern_id && (vorhaben.data?.length || a.vorhaben_id) ? (
          <Zeile symbol={Symbol.ressort} label="Vorhaben">
            <span className={d.reiheUmbruch}>
              <KatalogAuswahl
                art="vorhaben"
                wert={a.vorhaben_id}
                eintraege={a.vorhaben_id && vorhabenName && !vorhaben.data?.some((x) => x.id === a.vorhaben_id) ? [...(vorhaben.data ?? []), { id: a.vorhaben_id, name: vorhabenName }] : vorhaben.data}
                darfAnlegen={darfVorhaben && darf}
                gesperrt={!darfVorhabenAendern}
                ressortFuerVorhaben={a.ressort_id}
                onWechsel={(x) =>
                  speichern({ vorhaben_id: x }, () => {
                    // Ohne Vorhaben gibt es keine Kapsel: Die Datenbank setzt dann „Nur Vorstand“
                    if (!x && a.sichtbarkeit === "vorhaben") zeigen("Ohne Vorhaben gilt jetzt „Nur Vorstand“.");
                  })
                }
              />
              {a.vorhaben_id && vorhabenName ? (
                <Link to={`/vorhaben/${a.vorhaben_id}`} className={d.leiseLink}>
                  Vorhaben öffnen<span className="nur-leser"> {vorhabenName}</span>
                </Link>
              ) : null}
            </span>
          </Zeile>
        ) : null}
      </div>

      <section className={d.abschnitt} aria-labelledby="beschreibung-titel">
        <h3 id="beschreibung-titel">
          Beschreibung {gespeichert ? <span className={d.gespeichert}>Gespeichert</span> : null}
        </h3>
        <textarea
          className={d.beschreibung}
          aria-labelledby="beschreibung-titel"
          value={beschreibung}
          maxLength={10000}
          disabled={!darf}
          placeholder="Füge eine Beschreibung hinzu"
          onChange={(e) => setBeschreibung(e.target.value)}
          onBlur={() =>
            beschreibung !== a.beschreibung &&
            speichern({ beschreibung }, () => {
              setGespeichert(true);
              window.setTimeout(() => setGespeichert(false), 2000);
            })
          }
        />
        {a.herkunft ? <p className={d.meta}>Herkunft: {a.herkunft}</p> : null}
      </section>

      {!a.eltern_id ? (
        <Unteraufgaben a={a} liste={unteraufgaben} darf={darf} sperrGrund={hakenGesperrt(a, ich, vorstand, leitet)} sa={sa} sprint={kalenderSprint(a, sprints.data)} onOeffnen={onWechseln} onLoeschen={(u) => loeschen.loeschen(u, true)} />
      ) : null}

      <Links links={a.links} darf={darf} onSpeichern={(neu, meldung) => speichern({ links: neu }, () => zeigen(meldung))} />

      <Rueckfrage text={rueckfrage} onAntwort={(mit) => status("erledigt", mit)} onAbbrechen={() => setRueckfrage(null)} />
      {loeschen.rueckfrage}
    </div>
  );

  return <Rahmen links={links} rechts={<Aktivitaet aufgabe={a} personen={wahl.personen} gesperrt={gesperrt} fokus={fokusKommentar} />} />;
}

/* ------------------------------------------------------------------------------------------------------------------------------ */
/* Unteraufgaben im Dialog                                                                                                         */
/* ------------------------------------------------------------------------------------------------------------------------------ */

function Unteraufgaben({
  a,
  liste,
  darf,
  sperrGrund,
  sa,
  sprint,
  onOeffnen,
  onLoeschen,
}: {
  a: Aufgabe;
  liste: Aufgabe[];
  darf: boolean;
  /** Warum die Haken nicht gehen, wenn `darf` falsch ist */
  sperrGrund: string | null;
  sa: ReturnType<typeof useSchnellaktionen>;
  sprint: KalenderSprint | null;
  onOeffnen: (id: string) => void;
  /** Papierkorb: sofort, ohne Rückfrage, mit Rückgängig */
  onLoeschen: (u: Aufgabe) => void;
}) {
  const { akt } = sa;
  const wahl = useWaehlbar(darf, { vorhaben: a.vorhaben_id, sichtbarkeit: a.sichtbarkeit });
  const [neu, setNeu] = useState("");
  const feld = useRef<HTMLInputElement>(null);
  const f = fortschritt({ unteraufgaben_offen: liste.filter((u) => u.status !== "erledigt").length, unteraufgaben_gesamt: liste.length });

  function anlegen(e?: FormEvent) {
    e?.preventDefault();
    const titel = neu.trim();
    if (!titel) return;
    akt.anlegen.mutate(
      { titel, person: null, sprint: null, frist_datum: null, prioritaet: "normal", sichtbarkeit: a.sichtbarkeit, eltern: a.id, ressort: null, vorhaben: null },
      {
        onSuccess: () => {
          setNeu("");
          feld.current?.focus();
        },
        onError: sa.fehler,
      },
    );
  }

  return (
    <section className={d.abschnitt} aria-labelledby="unter-titel">
      <div className={d.abschnittKopf}>
        <h3 id="unter-titel">Unteraufgaben</h3>
        <span className={d.meta}>{f ? f.text : "noch keine"}</span>
      </div>
      {f ? (
        <div className={d.balken} aria-hidden="true">
          <span style={{ width: `${f.anteil * 100}%` }} />
        </div>
      ) : null}
      <ul className={d.unterListe}>
        {liste.map((u) => {
          const fertig = u.status === "erledigt";
          return (
            <li key={u.id} className={d.unterZeile}>
              <Haken an={fertig} titel={u.titel} gesperrt={darf ? null : sperrGrund} onUmschalten={() => sa.erledigtUmschalten(u)} />
              <button type="button" className={[d.unterTitel, fertig && d.durch].filter(Boolean).join(" ")} onClick={() => onOeffnen(u.id)}>
                {u.titel}
              </button>
              <span className={d.unterSteuer}>
                <PersonenAuswahl
                  art="stapel"
                  label={`Verantwortlich für „${u.titel}“`}
                  ausgewaehlt={u.personen}
                  personen={wahl.personen}
                  waehlbar={wahl.waehlbar}
                  entfernbar={wahl.entfernbar}
                  gesperrt={!darf}
                  onHinzu={(pid) => sa.personHinzu(u, pid)}
                  onWeg={(pid) => akt.personWeg.mutate({ id: u.id, person: pid }, { onError: sa.fehler })}
                />
                <Kalender
                  klein
                  label={`Frist für „${u.titel}“`}
                  wert={u.frist_datum}
                  leer="Frist"
                  entfernen="Frist entfernen"
                  sprint={sprint}
                  gesperrt={!darf}
                  onWahl={(tag) => akt.bearbeiten.mutate({ id: u.id, aenderung: { frist_datum: tag } }, { onError: sa.fehler })}
                />
                <select
                  className={[d.pille, d.klein, d[`p-${u.prioritaet}`]].filter(Boolean).join(" ")}
                  aria-label={`Priorität von „${u.titel}“`}
                  value={u.prioritaet}
                  disabled={!darf}
                  onChange={(e) => akt.bearbeiten.mutate({ id: u.id, aenderung: { prioritaet: e.target.value } }, { onError: sa.fehler })}
                >
                  {PRIO_LISTE.map((p) => (
                    <option key={p} value={p}>
                      {PRIORITAET[p]}
                    </option>
                  ))}
                </select>
                {darf ? (
                  <button type="button" className={d.papierkorb} aria-label={`Unteraufgabe „${u.titel}“ löschen`} title="Löschen" onClick={() => onLoeschen(u)}>
                    {PAPIERKORB}
                  </button>
                ) : null}
              </span>
            </li>
          );
        })}
      </ul>
      {darf ? (
        <form className={d.unterNeu} onSubmit={anlegen}>
          <label className="nur-leser" htmlFor="unter-neu">
            Neue Unteraufgabe
          </label>
          <input
            ref={feld}
            id="unter-neu"
            className={d.eingabe}
            value={neu}
            maxLength={200}
            placeholder="Unteraufgabe hinzufügen, Enter legt an"
            onChange={(e) => setNeu(e.target.value)}
          />
          <Knopf type="submit" disabled={akt.anlegen.isPending}>
            Hinzufügen
          </Knopf>
        </form>
      ) : null}
      <Rueckfrage text={sa.rueckfrage?.text ?? null} onAntwort={(mit) => sa.rueckfrageAntwort(mit)} onAbbrechen={sa.rueckfrageSchliessen} />
    </section>
  );
}

/* ------------------------------------------------------------------------------------------------------------------------------ */
/* Links                                                                                                                           */
/* ------------------------------------------------------------------------------------------------------------------------------ */

function Links({ links, darf, onSpeichern }: { links: Aufgabe["links"]; darf: boolean; onSpeichern: (neu: Aufgabe["links"], meldung: string) => void }) {
  const { zeigen } = useHinweis();
  const [url, setUrl] = useState("");
  const [label, setLabel] = useState("");

  function hinzufuegen(e: FormEvent) {
    e.preventDefault();
    const u = url.trim();
    if (!/^https:\/\/\S+$/.test(u)) {
      zeigen("Bitte einen Link mit https:// eingeben.", { fehler: true });
      return;
    }
    // Dieselben Grenzen prüft die Datenbank (aufgaben_links_pruefen, Schutzplan S3)
    if (u.length > 2000) {
      zeigen("Ein Link hat höchstens 2000 Zeichen.", { fehler: true });
      return;
    }
    if (links.length >= 20) {
      zeigen("Eine Aufgabe hat höchstens 20 Links.", { fehler: true });
      return;
    }
    onSpeichern([...links, { label: label.trim() || u.replace(/^https:\/\//, "").slice(0, 60), url: u }], "Link gespeichert.");
    setUrl("");
    setLabel("");
  }

  return (
    <section className={d.abschnitt} aria-labelledby="links-titel">
      <div className={d.abschnittKopf}>
        <h3 id="links-titel">Links</h3>
        <span className={d.meta}>Drive, Dokumente</span>
      </div>
      {links.length > 0 ? (
        <ul className={d.linkListe}>
          {links.map((l, i) => (
            <li key={`${l.url}-${i}`}>
              <a href={l.url} target="_blank" rel="noopener noreferrer">
                {l.label}
              </a>
              {darf ? (
                <button type="button" className={d.leiseKnopf} onClick={() => onSpeichern(links.filter((_, j) => j !== i), "Link entfernt.")}>
                  Entfernen<span className="nur-leser"> {l.label}</span>
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
      {darf ? (
        <form className={d.linkNeu} onSubmit={hinzufuegen}>
          <label className="nur-leser" htmlFor="link-url">
            Link
          </label>
          <input id="link-url" className={d.eingabe} type="url" placeholder="https://drive.google.com/…" value={url} onChange={(e) => setUrl(e.target.value)} />
          <label className="nur-leser" htmlFor="link-name">
            Name des Links
          </label>
          <input id="link-name" className={d.eingabe} placeholder="Name, zum Beispiel Handout" value={label} maxLength={80} onChange={(e) => setLabel(e.target.value)} />
          <Knopf type="submit">Link anfügen</Knopf>
        </form>
      ) : null}
    </section>
  );
}

/* ------------------------------------------------------------------------------------------------------------------------------ */
/* Entwurf: Neue Aufgabe in derselben Oberfläche                                                                                   */
/* ------------------------------------------------------------------------------------------------------------------------------ */

function Entwurf({ vorgabe, onSchliessen, onAngelegt }: { vorgabe: NeueAufgabeVorgabe; onSchliessen: () => void; onAngelegt: (id: string) => void }) {
  const sitzung = useAngemeldet();
  const ich = sitzung.person.id;
  const vorstand = sitzung.rechte.has("aufgaben.alle_bearbeiten");
  const darfPlanen = sitzung.rechte.has("sprint.planen");
  const darfVorhaben = sitzung.rechte.has("vorhaben.verwalten");
  const { zeigen } = useHinweis();
  const sprints = useSprints();
  const aufgaben = useAufgaben();
  const ressorts = useRessorts();
  const vorhaben = useVorhaben();
  const kapsel = useKapsel();
  const { anlegen, bearbeiten } = useAufgabenAktionen();

  const eltern = vorgabe.eltern ? aufgaben.data?.find((a) => a.id === vorgabe.eltern) : undefined;
  // Vorgaben wie in der Zeile „+ Aufgabe hinzufügen“ (vorgaben.ts). Das Ressort des Vorhabens kommt, sobald die Vorhaben geladen sind.
  const laufend = laufenderSprint(sprints.data);
  const start = startwerte(vorgabe, { ich, vorstand, darfPlanen, laufend, ressortVon: (id) => vorhaben.data?.find((x) => x.id === id)?.ressort_id });
  const [titel, setTitel] = useState("");
  const [beschreibung, setBeschreibung] = useState("");
  const [personen, setPersonen] = useState<string[]>(start.personen);
  const [frist, setFrist] = useState<string | null>(null);
  const [prioritaet, setPrioritaet] = useState<Prioritaet>(start.prioritaet);
  // Aus einer Kapsel: Ressort des Vorhabens als Vorgabe, bis jemand selbst wählt (wie früher die Schnellzeile der Kapsel)
  const [ressortWahl, setRessort] = useState<string | null | undefined>(undefined);
  const ressort = ressortWahl === undefined ? start.ressort : ressortWahl;
  const [vorhabenId, setVorhabenId] = useState<string | null>(start.vorhaben);
  const [wo, setWo] = useState<Wo>(start.wo);
  const [sichtbarkeit, setSichtbarkeit] = useState<Sichtbarkeit>(start.sichtbarkeit);
  const [unter, setUnter] = useState<string[]>([]);
  const [unterNeu, setUnterNeu] = useState("");
  const [links, setLinks] = useState<Aufgabe["links"]>([]);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  const imSprint = !!sprintFuer(wo, { darfPlanen, laufend }, !!eltern);
  const sicht = eltern ? eltern.sichtbarkeit : sichtbarkeit;
  const vorhabenFuer = eltern ? eltern.vorhaben_id : vorhabenId;
  const vorhabenName = kapsel.name(vorhabenFuer) ?? vorhaben.data?.find((x) => x.id === vorhabenFuer)?.name ?? null;
  const wahl = useWaehlbar(true, { vorhaben: vorhabenFuer, sichtbarkeit: sicht });
  const erste = wahl.personen?.find((p) => p.id === personen[0]);
  const vorschau = anlegenVorschau({
    ich,
    person: erste ? { id: erste.id, vorname: erste.vorname, vorstand: erste.vorstand } : null,
    sichtbarkeit: sicht,
    unteraufgabe: !!eltern,
    vorhaben: vorhabenName,
  });

  /** Vorhaben gewählt: „Kapsel“ wird Vorgabe. Ohne Vorhaben gibt es keine Kapsel (BAUPLAN-PAKETE.md, Abschnitt 9.5). */
  function vorhabenWaehlen(id: string | null) {
    setVorhabenId(id);
    if (id && sichtbarkeit !== "vorhaben") setSichtbarkeit("vorhaben");
    if (!id && sichtbarkeit === "vorhaben") setSichtbarkeit(grundSicht(vorstand));
  }
  const weitere = personen.slice(1).map((pid) => wahl.personen?.find((p) => p.id === pid)?.vorname).filter(Boolean);
  const last = personen
    .map((pid) => lastHinweis(aufgaben.data ?? [], pid, imSprint ? (laufend?.id ?? null) : null, prioritaet, wahl.personen?.find((p) => p.id === pid)?.vorname ?? ""))
    .find(Boolean);
  const hinweis = sichtHinweis(wahl.personen, personen, sicht);

  async function absenden() {
    setFehler(null);
    const t = titel.trim();
    if (!t) {
      setFehler("Was ist zu tun? Bitte einen Titel eingeben.");
      return;
    }
    setLaeuft(true);
    try {
      const id = await anlegen.mutateAsync({
        titel: t,
        person: personen[0] ?? null,
        personen,
        sprint: imSprint ? (laufend?.id ?? null) : null,
        frist_datum: frist,
        prioritaet,
        sichtbarkeit: sicht,
        eltern: eltern?.id ?? null,
        ressort: eltern ? null : ressort,
        vorhaben: eltern ? null : vorhabenId,
        beschreibung: beschreibung.trim(),
      });
      // Unteraufgaben nacheinander, damit die Reihenfolge stimmt. Sie haben keinen eigenen Sprint.
      if (!eltern) {
        for (const u of unter) {
          await anlegen.mutateAsync({ titel: u, person: null, sprint: null, frist_datum: null, prioritaet: "normal", sichtbarkeit: sicht, eltern: id, ressort: null, vorhaben: null });
        }
      }
      if (links.length > 0) await bearbeiten.mutateAsync({ id, aenderung: { links } });
      zeigen(mitPunkt(`„${t}“ angelegt${eltern ? "" : imSprint ? ` in ${laufend?.name ?? "den Sprint"}` : " im Backlog"}`));
      onAngelegt(id);
    } catch (err) {
      setFehler(fehlertext(err));
      setLaeuft(false);
    }
  }

  function unterHinzu(e?: FormEvent | KeyboardEvent) {
    e?.preventDefault();
    const t = unterNeu.trim().replace(/\s+/g, " ").slice(0, 200);
    if (!t) return;
    setUnter([...unter, t]);
    setUnterNeu("");
  }

  const brot = eltern ? `Unteraufgabe von „${eltern.titel}“` : imSprint ? (laufend?.name ?? "Sprint") : "Backlog";

  // Großes Modal wie eine bestehende Aufgabe, rechts die leere Aktivität (BAUPLAN-NEUE-AUFGABE-MODAL.md). Titel mit Rand (Feinschliff C).
  const linksSpalte = (
    <div className={d.inhalt}>
      <Kopf brot={brot} onSchliessen={onSchliessen} />
      <h2 id="dialog-name" className="nur-leser">
        Neue Aufgabe
      </h2>
      <div>
        <Titel wert={titel} platzhalter="Was ist zu tun?" rahmen onWechsel={setTitel} onFertig={() => undefined} />
        <p className={d.meta}>Enter im Titel legt an.</p>
      </div>

      <div className={d.eigenschaften}>
        <Zeile symbol={Symbol.status} label="Status">
          <span>Zu tun</span>
        </Zeile>
        <Zeile symbol={Symbol.person} label="Verantwortlich" breit>
          <PersonenAuswahl
            label="Verantwortlich für die neue Aufgabe"
            ausgewaehlt={personen}
            personen={wahl.personen}
            waehlbar={wahl.waehlbar}
            entfernbar={wahl.entfernbar}
            onHinzu={(pid) => setPersonen((p) => (p.includes(pid) ? p : [...p, pid]))}
            onWeg={(pid) => setPersonen((p) => p.filter((x) => x !== pid))}
          />
          {!vorstand ? (
            <span className={d.meta}>
              {wahl.leitet && sicht !== "vorstand" ? "Als Leitung gibst du Aufgaben an Personen aus dem Vorhaben." : "Mitglieder legen Aufgaben für sich oder offen an."}
            </span>
          ) : null}
        </Zeile>
        <Zeile symbol={Symbol.frist} label="Frist">
          <Kalender
            label="Frist"
            wert={frist}
            leer="Frist setzen"
            entfernen="Frist entfernen"
            sprint={laufend ? { beginn: laufend.beginn, ende: laufend.ende, name: laufend.name } : null}
            zusatz={!frist && imSprint && laufend ? `ohne Datum gilt Sprintende ${laufend.name.replace("Sprint ", "")}` : undefined}
            onWahl={setFrist}
          />
        </Zeile>
        <Zeile symbol={Symbol.prio} label="Priorität">
          <Wertwahl art="Priorität" optionen={PRIO_OPTIONEN} wert={prioritaet} onWechsel={setPrioritaet} />
        </Zeile>
        {eltern ? (
          <Zeile symbol={Symbol.ressort} label="Ressort, Wo, Sichtbar">
            <span>wie die Aufgabe</span>
          </Zeile>
        ) : (
          <>
            <Zeile symbol={Symbol.ressort} label="Ressort">
              <KatalogAuswahl art="ressort" wert={ressort} eintraege={ressorts.data} darfAnlegen={darfPlanen} onWechsel={setRessort} />
            </Zeile>
            <Zeile symbol={Symbol.wo} label="Wo">
              {darfPlanen ? (
                <Wertwahl art="Wo" optionen={woWerte(laufend?.name ?? null)} wert={laufend ? wo : "backlog"} onWechsel={setWo} />
              ) : (
                <span>Backlog. In den Sprint holt sie der Vorstand.</span>
              )}
            </Zeile>
            {vorhaben.data?.length ? (
              <Zeile symbol={Symbol.ressort} label="Vorhaben">
                <KatalogAuswahl art="vorhaben" wert={vorhabenId} eintraege={vorhaben.data} darfAnlegen={darfVorhaben} ressortFuerVorhaben={ressort} onWechsel={vorhabenWaehlen} />
              </Zeile>
            ) : null}
            <Zeile symbol={Symbol.sicht} label="Sichtbar für" breit>
              {sichtOptionen(!!vorhabenId, vorstand).length > 1 ? (
                <Wertwahl art="Sichtbar für" optionen={sichtWerte(sichtOptionen(!!vorhabenId, vorstand), vorhabenName)} wert={sichtbarkeit} onWechsel={setSichtbarkeit} />
              ) : (
                <span>{SICHTBARKEIT[sichtbarkeit]}</span>
              )}
              <span className={d.meta}>{sichtErklaerung(sichtbarkeit, vorhabenName)}</span>
            </Zeile>
          </>
        )}
      </div>

      <section className={d.abschnitt} aria-labelledby="beschreibung-neu">
        <h3 id="beschreibung-neu">Beschreibung</h3>
        <textarea
          className={d.beschreibung}
          aria-labelledby="beschreibung-neu"
          value={beschreibung}
          maxLength={10000}
          placeholder="Füge eine Beschreibung hinzu"
          onChange={(e) => setBeschreibung(e.target.value)}
        />
      </section>

      {!eltern ? (
        <section className={d.abschnitt} aria-labelledby="unter-neu-titel">
          <div className={d.abschnittKopf}>
            <h3 id="unter-neu-titel">Unteraufgaben</h3>
            <span className={d.meta}>{unter.length ? `${unter.length} vorgemerkt` : "noch keine"}</span>
          </div>
          {unter.length > 0 ? (
            <ul className={d.unterListe}>
              {unter.map((u, i) => (
                <li key={`${u}-${i}`} className={d.unterZeile}>
                  <span className={d.unterTitel}>{u}</span>
                  <button type="button" className={d.leiseKnopf} onClick={() => setUnter(unter.filter((_, j) => j !== i))}>
                    Löschen<span className="nur-leser"> „{u}“</span>
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
          <div className={d.unterNeu}>
            <label className="nur-leser" htmlFor="unter-vorab">
              Unteraufgabe vormerken
            </label>
            <input
              id="unter-vorab"
              className={d.eingabe}
              value={unterNeu}
              maxLength={200}
              placeholder="Unteraufgabe hinzufügen, Enter legt an"
              onChange={(e) => setUnterNeu(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") unterHinzu(e);
              }}
            />
            <Knopf onClick={() => unterHinzu()}>Hinzufügen</Knopf>
          </div>
          <p className={d.meta}>Eine Unteraufgabe ist eine eigene Aufgabe: Nach dem Anlegen öffnet ein Klick auf den Titel sie mit Personen, Frist, Status und Kommentaren.</p>
        </section>
      ) : null}

      <Links links={links} darf onSpeichern={(neu) => setLinks(neu)} />

      <div className={[ui.innen, d.kasten].join(" ")}>
        <h3>Das passiert beim Anlegen</h3>
        <ul className={d.vorschau}>
          {vorschau.map((z) => (
            <li key={z}>{z}</li>
          ))}
          {weitere.length ? <li>{weitere.join(", ")} {weitere.length === 1 ? "bekommt" : "bekommen"} eine Mitteilung: hinzugefügt.</li> : null}
          {!eltern ? <li>{imSprint ? mitPunkt(`Sie liegt in ${laufend?.name ?? "dem laufenden Sprint"}`) : "Sie liegt im Backlog."}</li> : null}
          {!eltern && unter.length > 0 ? <li>{unter.length === 1 ? "Dazu eine Unteraufgabe." : `Dazu ${unter.length} Unteraufgaben.`}</li> : null}
        </ul>
        {hinweis ? <p className={d.meta}>{hinweis}</p> : null}
        {last ? <Meldung>{last}</Meldung> : null}
      </div>
      {fehler ? <Meldung fehler>{fehler}</Meldung> : null}
      <div className={d.fuss}>
        <Knopf onClick={onSchliessen}>Abbrechen</Knopf>
        {/* Bis der laufende Sprint geladen ist, stünde „Wo?“ sonst kurz auf Backlog */}
        <Knopf art="primaer" ohnePunkt disabled={laeuft || (darfPlanen && sprints.isPending)} onClick={() => void absenden()}>
          {laeuft ? "Wird angelegt …" : "Anlegen"}
        </Knopf>
      </div>
    </div>
  );

  // Enter im Titel legt an
  return (
    <div
      className={d.entwurf}
      onKeyDown={(e) => {
        if (e.key === "Enter" && (e.target as HTMLElement).id === "aufgabe-titel") {
          e.preventDefault();
          void absenden();
        }
      }}
    >
      <Rahmen
        links={linksSpalte}
        rechts={
          <div className={d.aktivitaet}>
            <div className={d.aktKopf}>
              <h3>Aktivität</h3>
            </div>
            <div className={d.zeitleiste}>
              <p className={d.meta}>Noch nichts passiert.</p>
            </div>
            <div className={d.eingabeBereich}>
              <p className={d.meta}>Kommentare gehen, sobald die Aufgabe angelegt ist.</p>
            </div>
          </div>
        }
      />
    </div>
  );
}
